export interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface OpenAIResponse {
  id: string;
  content: string;
  error?: string;
}

class OpenAIService {
  private static instance: OpenAIService;
  private apiKey: string | null = null;
  private isConfigured = false;
  private baseUrl = 'https://api.openai.com/v1';
  // Default API key for development/testing - in production, use environment variables
  private defaultApiKey = import.meta.env.VITE_OPENAI_API_KEY || '';

  private constructor() {
    // Try to load the API key from environment variables
    this.loadApiKey();
  }

  static getInstance(): OpenAIService {
    if (!OpenAIService.instance) {
      OpenAIService.instance = new OpenAIService();
    }
    return OpenAIService.instance;
  }

  /**
   * Load API key from environment variables or default settings
   */
  private loadApiKey(): void {
    try {
      // First check for a developer-configured API key in localStorage
      const savedApiKey = localStorage.getItem('developer-openai-api-key');
      
      if (savedApiKey) {
        this.configure(savedApiKey);
        return;
      }
      
      // Then check for environment variable
      if (this.defaultApiKey) {
        this.configure(this.defaultApiKey);
        return;
      }
      
      // If no API key is available, we'll stay in mock mode
      console.info('No OpenAI API key found. FlexHelp AI will operate in mock mode.');
    } catch (error) {
      console.warn('Error accessing API key:', error);
    }
  }

  /**
   * Configure the OpenAI service with an API key
   * This should only be used by developers/administrators
   */
  configure(apiKey: string): void {
    this.apiKey = apiKey;
    this.isConfigured = true;
    
    // Save the developer API key
    try {
      localStorage.setItem('developer-openai-api-key', apiKey);
    } catch (error) {
      console.warn('Error saving API key to localStorage:', error);
    }
  }

  /**
   * Check if the service is properly configured
   */
  isReady(): boolean {
    // Temporarily disable embeddings due to quota issues
    return false;
    // Original code:
    // return this.isConfigured && !!this.apiKey;
  }

  /**
   * Check if the service has a developer-configured API key
   */
  hasDeveloperKey(): boolean {
    try {
      return !!localStorage.getItem('developer-openai-api-key');
    } catch (error) {
      return false;
    }
  }

  /**
   * Generate embeddings for text using OpenAI's embedding API
   */
  async createEmbedding(text: string): Promise<number[]> {
    try {
      if (!this.isReady()) {
        // For mock mode, return an empty vector
        return new Array(1536).fill(0); // OpenAI embeddings are 1536-dimensional
      }

      const response = await fetch(`${this.baseUrl}/embeddings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'text-embedding-3-small',
          input: text
        })
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: { message: string } };
        const errorMessage = errorData.error?.message || 'Failed to generate embeddings';
        
        // Check for quota exceeded error and handle it gracefully
        if (errorMessage.includes('exceeded your current quota') || errorMessage.includes('billing')) {
          console.warn('OpenAI quota exceeded. Disabling embeddings temporarily.');
          // Temporarily disable embeddings for this session
          this.isConfigured = false;
          return new Array(1536).fill(0);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json() as { 
        data: Array<{ embedding: number[] }>;
      };
      
      return data.data[0].embedding;
    } catch (error) {
      console.error('Error generating embeddings:', error);
      // Return empty vector on error
      return new Array(1536).fill(0);
    }
  }

  /**
   * Generate text completion from OpenAI
   */
  async generateCompletion(messages: OpenAIMessage[], options?: { 
    model?: string;
    temperature?: number;
    maxTokens?: number;
  }): Promise<OpenAIResponse> {
    try {
      if (!this.isReady()) {
        // Instead of failing, provide a mock response when not configured
        return this.getMockResponse(messages);
      }

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: options?.model || 'gpt-4o-mini',
          messages,
          temperature: options?.temperature || 0.7,
          max_tokens: options?.maxTokens || 500,
        })
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: { message: string } };
        throw new Error(errorData.error?.message || 'Failed to get response from OpenAI');
      }

      interface OpenAICompletionResponse {
        id: string;
        choices: Array<{ message: { content: string } }>;
      }
      
      const data = await response.json() as OpenAICompletionResponse;
      
      return {
        id: data.id,
        content: data.choices[0].message.content,
      };
    } catch (error) {
      console.error('Error generating AI completion:', error);
      return {
        id: 'error',
        content: '',
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Helper method to generate responses when the API isn't configured
   */
  private getMockResponse(messages: OpenAIMessage[]): OpenAIResponse {
    // Extract the last user message
    const lastUserMessage = messages.filter(m => m.role === 'user').pop();
    const userQuery = lastUserMessage?.content.toLowerCase() || '';

    let response = '';

    // Add contextual responses based on common queries
    if (userQuery.includes('maintenance') || userQuery.includes('repair')) {
      response = "For maintenance requests, you can submit a service request through the app. Just navigate to the 'Service Requests' section and click 'Create New Request'. Would you like me to help you with that?";
    } else if (userQuery.includes('compliance') || userQuery.includes('regulation')) {
      response = "Regarding compliance, all properties need to adhere to local regulations. This includes building codes, tenant rights, and safety standards. For specific regulations in your area, I recommend checking with your local housing authority.";
    } else if (userQuery.includes('lease') || userQuery.includes('contract')) {
      response = "Lease agreements should be carefully reviewed to ensure they meet legal requirements. The standard lease template in FlexHelp includes all required disclosures and terms. You can customize it in the Contracts section of the admin dashboard.";
    } else if (userQuery.includes('billing') || userQuery.includes('payment')) {
      response = "FlexHelp offers multiple payment options for tenants, including online payments, automatic bank transfers, and credit card processing. Property owners can view all transaction history in the Billing section of their dashboard.";
    } else if (userQuery.includes('help') || userQuery.includes('how do i')) {
      response = "I'm here to help you navigate the platform, manage properties, and coordinate with service providers. What specific area do you need assistance with?";
    } else {
      response = "I can assist with maintenance guidance, compliance questions, lease agreements, billing inquiries, and general property management. Please let me know what you'd like to learn more about.";
    }

    return {
      id: 'response-' + Date.now(),
      content: response
    };
  }

  async transcribeAudio(audioBlob: Blob): Promise<string> {
    try {
      if (!this.isReady()) {
        // Return a mock response instead of error when not configured
        console.log('Using mock transcription service');
        return "This is a simulated voice transcription. The actual transcription service requires an OpenAI API key.";
      }

      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      formData.append('model', 'whisper-1');
      formData.append('language', 'en');

      const response = await fetch(`${this.baseUrl}/audio/transcriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: { message: string } };
        throw new Error(errorData.error?.message || 'Failed to transcribe audio');
      }

      const data = await response.json() as { text: string };
      return data.text;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  }
}

export const openAIService = OpenAIService.getInstance(); 